import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import Input from '../components/Input';
import Button from '../components/Button';

const JoinAssignment: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [accessCode, setAccessCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!accessCode.trim()) {
      setError('Access code is required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // First, find the assignment with this access code
      const { data: assignments, error: findError } = await supabase
        .from('assignments')
        .select('id')
        .eq('access_code', accessCode.toUpperCase())
        .limit(1);
      
      if (findError) throw findError;
      
      if (!assignments || assignments.length === 0) {
        setError('Invalid access code. Please check and try again.');
        setLoading(false);
        return;
      }
      
      const assignmentId = assignments[0].id;
      
      // Check if user has already joined this assignment
      const { data: existingAccess, error: checkError } = await supabase
        .from('assignment_access')
        .select('id')
        .eq('user_id', user?.id)
        .eq('assignment_id', assignmentId)
        .limit(1);
      
      if (checkError) throw checkError;
      
      if (existingAccess && existingAccess.length > 0) {
        // User has already joined this assignment
        navigate(`/assignment/${assignmentId}`);
        return;
      }
      
      // Add user to assignment_access
      const { error: joinError } = await supabase
        .from('assignment_access')
        .insert([
          {
            user_id: user?.id,
            assignment_id: assignmentId,
            joined_at: new Date().toISOString()
          }
        ]);
      
      if (joinError) throw joinError;
      
      // Redirect to the assignment page
      navigate(`/assignment/${assignmentId}`);
      
    } catch (error: any) {
      console.error('Error joining assignment:', error);
      setError(error.message || 'Failed to join assignment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-md mx-auto">
        <h1 className="text-3xl font-bold text-primary mb-8 text-center">Join Assignment</h1>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-6">
            <Input
              id="accessCode"
              label="Assignment Access Code"
              type="text"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder="Enter 6-digit code (e.g., ABC123)"
              required
            />
            <p className="text-sm text-gray-500 mt-1">
              Enter the access code provided by your instructor
            </p>
          </div>
          
          <div className="flex justify-end space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? 'Joining...' : 'Join Assignment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JoinAssignment;
